import React, { MutableRefObject, RefAttributes, useEffect, useRef, useState } from "react";
import { ISerializedUIField, IUIPropertyDefinitionMap, IWidgetTemplateProps, PropertyPath, addToPath, registerCardTemplate, registerMobileCardTemplate } from "../../../common/common";
import { SearchBox, ToggleFilter, WidgetWrapper } from "uxp/components";
import classNames from "classnames";
import { generateFullUUID, hasValue, replaceWithData, updateParentUIPropsWithDefaultWidgetUIProps } from "../../../../util";
import { ContainerProperty, extractContainerProperties } from "../../../common/ui-props/ContainerProperty";
import { TextProperty, extractTextProperties } from "../../../common/ui-props/TextProperty";
import { ISelectableItemInstanceProps, SelectableItem, selectUIPropsForMobileTempaltes } from "../../../common/SelectableItem";
import { IDataSource, IDataSourceBinding, executeAndBind } from "../../../common/data-source-picker/datasource";
import './SearchWidget.scss';
import { SearchTabsProperty } from "./SearchTabsProperty";
import { ItemCard2 } from "../../../common/ui-fields/item-card-field/ItemCard";
import { IAction } from "../../../common/action-editor/ActionEditor";
import MemorizedList from "../../cards/item-list-widget/MemorizedList";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { StaticFieldsList } from "../../../common/StaticFieldsList";
import { FieldContainer } from "../../../common/FieldContainer";
import jp from 'jsonpath'
import { BooleanGroupProperty } from "../../../common/ui-props/BooleanGroupProperty";
import { appendRowToContextData, appendWidgetDatasources, useInitialContextData } from "../../../../hooks/useContextData";

interface ISortOption {
    label: string;
    field: string;
    descending: boolean;
}
export interface ISearchTab {
    id: string,
    title: string,
    datasources?: IDataSource[]
    binding?: IDataSourceBinding
    hasFilters?: boolean,
    sortOptions: ISortOption[],
    filter: {
        fields: ISerializedUIField[],
        fillContainer: boolean,
        enableClear: boolean,
        enableExport: boolean
    },
    categoryFilter?: string,
    hasMapView?: boolean,
    searchResultTile: {
        imageField?: string,
        imagePosition?: string,
        imageSize?: string,
        imageType?: 'icon' | 'image',
        _imageSize?: number,
        imageBorderType?: 'circular' | 'square'
        imageBackgroundColor?: string,
        imageIcon?: string,
        imageIconColor?: string,
        borderColor?: string,
        topLabels?: any[],
        bottomLabels?: any[],
        buttons?: any[],
        titleField?: string,
        titleColor?: string,
        titleSize?: string,
        subTitleField?: string,
        subTitleColor?: string,
        subTitleSize?: string,
        latitudeField?: string,
        longitudeField?: string,
        onClick?: IAction,
        fields?: ISerializedUIField[],
    },
    noResultMessage?: string,
    noResultImage?: string
}

export const defaultTab: ISearchTab = {
    id: '_id',
    title: '_title',
    searchResultTile: {
        imageField: '{$.row.image}',
        imagePosition: 'center',
        imageSize: 'cover',
        titleField: '{$.row.title}',
        titleColor: '#424242',
        titleSize: '12px',
        subTitleField: '{$.row.subTitle}',
        subTitleColor: '#424242',
        subTitleSize: '11px',
        latitudeField: '{$.row.coordinates.latitude}',
        longitudeField: '{$.row.coordinates.longitude}',
        fields: [],
        onClick: { type: 'open-url' },
        imageType: 'image',
        _imageSize: 0,
        imageBorderType: 'circular',
        imageBackgroundColor: 'black',
        imageIcon: '',
        imageIconColor: 'black',
        topLabels: [],
        bottomLabels: [],
        buttons: []

    },
    binding: null,
    hasFilters: true,
    hasMapView: true,
    filter: {
        fields: [],
        fillContainer: false,
        enableClear: true,
        enableExport: true
    },
    categoryFilter: '',
    datasources: [],
    sortOptions: [],
}

const defaultItem: any = {
    image: 'https://unsplash.it/250',
    title: 'Title',
    subTitle: 'Sub title',
    coordinates: {
        latitude: '1.290270',
        longitude: '103.851959'
    }
}

export function generateSearchTabs(uiProps: any) {
    const tabs = uiProps?.tabs || []
    if (!tabs || tabs.length == 0) return Array(2).fill(0).map((a, i) => ({ ...structuredClone(defaultTab), id: generateFullUUID(), title: `Tab ${i + 1}` }))
    return tabs
}
const SearchWidget: React.FunctionComponent<IWidgetTemplateProps> = (props) => {

    const { designer, uxpContext, instanceId, index } = props
    const isDesignMode = props.designer && (props.designer.mode == 'preview' || props.designer.mode == 'thumbnail')

    let uiProps = props?.uiProps || {};
    uiProps.tabs = generateSearchTabs(uiProps)
    // need to pass the global uiProps to selectable
    const parentUIProps = updateParentUIPropsWithDefaultWidgetUIProps(props.parentUIProps, uiProps, index)

    const [tabs, setTabs] = useState<ISearchTab[]>(uiProps.tabs || [])
    const [selectedTab, setSelectedTab] = useState<ISearchTab>(null)
    const [query, setQuery] = useState('')
    const [contextData, setContextData] = useState({})
    const [view, setView] = useState<'list' | 'map'>('list')
    const [showFilters, setShowFilter] = useState(false)

    const selectedTabIndex = tabs.findIndex(t => t.id == selectedTab?.id)

    const dataSourcesPath: PropertyPath = designer?.isMobile ? addToPath('fields', index, 'props', 'uiProps', 'tabs', selectedTabIndex, 'datasources') : 'datasources'

    const selectableItemRef = useRef<ISelectableItemInstanceProps>(null)

    const XPRProps: IUIPropertyDefinitionMap = {
        'container': ContainerProperty({
            background: {
                image: {
                    image: '',
                    size: '',
                    position: '',
                },
                color: ''
            },
            removeShadow: false
        }),
        'headerContainer': ContainerProperty({
            background: {
                color: '',
                image: {
                    image: "",
                    position: "",
                    size: "",
                },
            },
            text: { color: '', fontSize: '12px' }
        }, 'Header'),
        'searchDisplayOptions': BooleanGroupProperty('Display Options',
            { 
                showSearchBox: true,
                showTabs: true,
             },
            { 
                showSearchBox: 'Show Search Box' ,
                showTabs:'Show Tabs',
            }),

        'tabs': SearchTabsProperty(uiProps.tabs || [], dataSourcesPath)
    }

    const { styles: containerStyles } = extractContainerProperties(uiProps.container)
    const { styles: headerStyles } = extractContainerProperties(uiProps.headerContainer)

    const { isLoading, initialContextData } = useInitialContextData(props)

    React.useEffect(() => {
        setContextData(prev => ({ ...prev, ...initialContextData }))
    }, [isLoading, initialContextData])

    React.useEffect(() => {
        console.log('context_data_________', contextData)
    }, [contextData])

    useEffect(() => {
        setTabs(uiProps.tabs || [])
    }, [uiProps])

    useEffect(() => {
        if (!selectedTab) {
            setSelectedTab(tabs[0])
        }
    }, [tabs, selectedTab])

    useEffect(() => {
        selectableItemRef?.current?.triggerSelect()
    }, [selectedTab])

    useEffect(() => {
        appendSelectedTabDataOnLive()
    }, [designer, selectedTab])

    async function appendSelectedTabDataOnLive() {
        if (!designer || !(designer.mode == 'preview' || designer.mode == 'thumbnail')) {
            const _contextData = await appendWidgetDatasources(contextData, props, [selectedTab?.binding])
            setContextData(_contextData)
        }
    }

    const getData = React.useCallback(async (max: number, lastToken?: string) => {

        return new Promise<{ items: any[], pageToken: string }>(async (done, nope) => {

            if (isDesignMode) {
                done({ items: [{ ...structuredClone(defaultItem) }], pageToken: '1' })
                return
            }
            let last = 0
            if (!hasValue(lastToken)) last = Number(lastToken)

            const params: any = {
                max: max,
                last: last
            }

            executeAndBind(props.uxpContext, uiProps.source, uiProps.datasources, params, params)
                .then(res => {
                    const items = res;
                    done({ items, pageToken: (last + items.length).toString() })
                })
                .catch(e => {
                    done({ items: [], pageToken: lastToken })
                })
        })
    }, [props.designer, contextData, uiProps])


    function renderItem(item: any, key: number) {
        const itemData = { ...structuredClone(contextData), row: item }

        return <ItemCard2
            imageField={selectedTab?.searchResultTile?.imageField}
            imageType={selectedTab?.searchResultTile?.imageType}
            imageBorderType={selectedTab?.searchResultTile?.imageBorderType}
            imageIcon={selectedTab?.searchResultTile?.imageIcon}
            imageIconColor={selectedTab?.searchResultTile?.imageIconColor}
            imageBackgroundColor={selectedTab?.searchResultTile?.imageBackgroundColor}
            imageSize={selectedTab?.searchResultTile?._imageSize?.toString()}
            titleField={selectedTab?.searchResultTile?.titleField}
            titleColor={selectedTab?.searchResultTile?.titleColor}
            titleSize={selectedTab?.searchResultTile?.titleSize}
            subTitleField={selectedTab?.searchResultTile?.subTitleField}
            subTitleColor={selectedTab?.searchResultTile?.subTitleColor}
            subTitleSize={selectedTab?.searchResultTile?.subTitleSize}
            topLabels={selectedTab?.searchResultTile?.topLabels || []}
            bottomLabels={selectedTab?.searchResultTile?.bottomLabels || []}
            buttons={selectedTab?.searchResultTile?.buttons || []}
            borderColor={''}
            onClick={selectedTab?.searchResultTile?.onClick}
            replacer={s => replaceWithData(s || '', itemData)}
        />
    }


    function renderSelectedTab() {

        const hasCategoryFilter = (hasValue(selectedTab?.categoryFilter))

        if (!selectedTab) return null
        return <div className={classNames("uxp-search-widget-content-container", { 'has-category-section': hasCategoryFilter })}
            onClick={e => { e.preventDefault(); e.stopPropagation() }}
        >
            {hasCategoryFilter &&
                <div className="uxp-search-widget-content-container-header">

                </div>
            }
            <div className="uxp-search-widget-content-container-content">
                {
                    view == 'list'
                        ?
                        <MemorizedList
                            data={getData}
                            pageSize={20}
                            renderItem={renderItem}
                            className="search-widget-data-list"
                            showEndOfContent={false}
                            showFooter={false}
                        />
                        : <></>
                }
            </div>

            {showFilters && <div className="uxp-search-widget-filters-container">
                <div className={classNames("uxp-search-widget-filters-content")}>

                    <div className="uxp-search-widget-filters-close-btn"
                        onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            setShowFilter(false)
                        }}
                    >
                        <FontAwesomeIcon icon={['fas', 'times']} />
                    </div>

                    <FieldContainer
                        designer={designer}
                        uxpContext={uxpContext}
                        contextData={contextData}
                        onChangeData={props.onChangeContextData}
                        uiProps={parentUIProps}
                        dataKey={'filters'}
                        droppableContainerId={selectedTab.id}
                        droppableParentContainerId={props.droppableParentContainerId}
                        propPath={designer?.isMobile ? ['fields', index, 'props', 'uiProps', 'tabs', selectedTabIndex, 'filter', 'fields'] : ['tabs', selectedTabIndex, 'filter', 'fields']}
                        disabled={props.disableDrop || false}
                        onChangeFields={(newFilter) => {
                            jp.apply(uiProps, `$.tabs[${selectedTabIndex}].filter.fields`, () => newFilter)
                            if (designer?.isMobile) {
                                designer?.setMobileCardUIProps(instanceId, uiProps, 'fields')
                            }
                            else {
                                designer?.setUIProps(Object.assign({}, uiProps))
                            }
                        }}
                    />
                </div>
            </div>}

            {(selectedTab?.hasFilters || selectedTab?.hasMapView) &&

                <div className="uxp-search-view-changer">
                    {
                        selectedTab?.hasMapView &&
                        <div className={classNames("uxp-search-toggle")}
                            onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                setView(prev => (prev == 'list' ? 'map' : 'list'))
                            }}
                        >
                            <div className="icon">
                                <FontAwesomeIcon icon={['fas', view == 'list' ? 'map' : 'list']} />
                            </div>
                            <div className="label">{view == 'list' ? 'Map' : 'List'}</div>
                        </div>
                    }
                    {
                        selectedTab?.hasFilters &&
                        <div className="uxp-search-toggle"
                            onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                setShowFilter(prev => !prev)
                            }}
                        >
                            <div className="icon">
                                <FontAwesomeIcon icon={['fas', 'sliders-h']} />
                            </div>
                            <div className="label">Filters</div>
                        </div>
                    }
                </div>
            }

        </div>
    }

    return <WidgetWrapper className={classNames("uxp-search-widget-wrapper", { 'no-shadow': uiProps?.container?.removeShadow, 'web-view': !designer?.isMobile })}>
        <div className="container" style={containerStyles} >
            <div className="header" style={headerStyles}>
                <SelectableItem
                    ref={selectableItemRef}
                    designer={designer}
                    props={selectUIPropsForMobileTempaltes(parentUIProps, XPRProps, index, { datasources: dataSourcesPath }, 'headerContainer', 'searchDisplayOptions', 'tabs')}
                    elementId={instanceId}
                    contextData={appendRowToContextData({ ...contextData, selectedTab: selectedTab }, props, selectedTab?.binding)}
                >
                    {uiProps?.searchDisplayOptions?.showTabs != false && <div className="search-widget-tabs-list">
                        {
                            tabs.map((t, i) => {
                                return <div className={classNames("search-widget-tab", { 'selected': selectedTab?.id === t.id })}
                                    onClick={e => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        setSelectedTab(t)
                                    }}
                                >
                                    {t.title}
                                </div>
                            })
                        }
                    </div>}

                    {(!(uiProps?.searchDisplayOptions?.showSearchBox == false)) && <div className="search-widget-search-box">
                        <SearchBox value={query} onChange={setQuery} />
                    </div>}

                </SelectableItem>
            </div>

            <div className="body">
                {renderSelectedTab()}
            </div>
        </div>
    </WidgetWrapper>
}

registerCardTemplate({
    id: 'search-widget',
    template: SearchWidget,
    name: 'Search Widget',
    description: 'Configurable search widget',
    complexity: 'medium',
    layout: {
        w: 9,
        h: 14
    },
    canDragDropFields: true,
    icon: "search"
});

registerMobileCardTemplate({
    id: 'search-widget',
    template: SearchWidget,
    name: 'Search Widget',
    description: 'Configurable search widget'
})